import React from 'react';
import { Link } from 'react-router-dom';
import { SOCIAL_LINKS, RESUME_URL, NAV_ITEMS } from '../data/constants';

const TOP_PROJECTS = [
  { id: 'swetrix', label: 'Swetrix' },
  { id: 'lifegraph', label: 'LifeGraph' },
  { id: 'skillgrid', label: 'SkillGrid' },
  { id: 'chronolex', label: 'Chronolex' },
];

const Footer = () => (
  <footer className="border-t border-border/40">
    {/* Gradient divider */}
    <div className="h-px w-full gradient-line" aria-hidden="true" />

    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
        {/* Col 1: Brand + Social */}
        <div>
          <span className="font-mono text-sm font-bold tracking-tight text-txt">
            yehor<span className="text-green">_</span>
          </span>
          <p className="text-sm text-txt-secondary mt-3 leading-relaxed max-w-xs">
            Senior Software Engineer building scalable systems across fintech, analytics, and legal tech.
          </p>
          <div className="flex items-center gap-1 mt-5 -ml-2.5">
            <a
              href={SOCIAL_LINKS.github}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center rounded-md text-txt-tertiary hover:text-green hover:bg-raised/60 transition-colors"
              aria-label="GitHub"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
            </a>
            <a
              href={SOCIAL_LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center rounded-md text-txt-tertiary hover:text-green hover:bg-raised/60 transition-colors"
              aria-label="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
            <a
              href={`mailto:${SOCIAL_LINKS.email}`}
              className="w-11 h-11 flex items-center justify-center rounded-md text-txt-tertiary hover:text-green hover:bg-raised/60 transition-colors"
              aria-label="Email"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Col 2: Quick Links */}
        <div>
          <h3 className="text-xs font-mono font-medium text-txt-tertiary uppercase tracking-widest mb-4">Quick Links</h3>
          <ul className="space-y-0.5">
            {NAV_ITEMS.map(({ id, label }) => (
              <li key={id}>
                <Link
                  to={`/#${id}`}
                  className="block py-1.5 text-sm text-txt-secondary hover:text-green transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
            <li>
              <a
                href={RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block py-1.5 text-sm text-txt-secondary hover:text-green transition-colors"
              >
                CV
              </a>
            </li>
            <li>
              <a
                href="/docs/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-1.5 text-sm text-txt-secondary hover:text-green transition-colors"
              >
                Docs
              </a>
            </li>
          </ul>
        </div>

        {/* Col 3: Top Projects */}
        <div>
          <h3 className="text-xs font-mono font-medium text-txt-tertiary uppercase tracking-widest mb-4">Top Projects</h3>
          <ul className="space-y-0.5">
            {TOP_PROJECTS.map(({ id, label }) => (
              <li key={id}>
                <Link
                  to={`/projects/${id}`}
                  className="block py-1.5 text-sm text-txt-secondary hover:text-green transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t border-border/40">
      <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-txt-tertiary">
          &copy; {new Date().getFullYear()} Yehor Dremliuha
        </p>
        <p className="text-xs text-txt-tertiary font-mono">
          Built with React & Tailwind CSS
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
